// ============================================================
// Organization roles + team limits — the single point of change for who
// may do what inside an organization (tenant). Used by the API
// (JWT principals, member management, invites), the dashboard (what to
// show) and the authz matrix test.
//
//   owner   everything, incl. billing, ownership transfer, deleting the org
//   admin   everything but ownership / org deletion: members, keys, config
//   member  read analytics + write analytics config (goals, funnels, destinations…)
//   viewer  read-only
//
// `analyst` is a legacy stored role (pre-team-management). It always had
// read-only analytics access, so it maps onto `viewer`, never upwards.
// ============================================================
import { PLANS, normalizePlan } from './index.js';
export const ORG_ROLES = ['owner', 'admin', 'member', 'viewer'];
/** Roles an owner/admin can hand out through an invite or a role change (not `owner`). */
export const ASSIGNABLE_ORG_ROLES = ['admin', 'member', 'viewer'];
/** Any stored role string → an OrgRole. Unknown / legacy values never gain rights. */
export function normalizeOrgRole(role) {
    if (role && ORG_ROLES.includes(role)) {
        return role;
    }
    return 'viewer';
}
export function isAssignableOrgRole(role) {
    return typeof role === 'string' && ASSIGNABLE_ORG_ROLES.includes(role);
}
/** v2 project scopes a dashboard user gets from their org role. */
export const ORG_ROLE_SCOPES = {
    owner: ['read', 'write', 'admin'],
    admin: ['read', 'write', 'admin'],
    member: ['read', 'write'],
    viewer: ['read'],
};
/** Org owners/admins: manage members, invites, keys, management keys, audit log. */
export function canManageOrg(role) {
    const r = normalizeOrgRole(role);
    return r === 'owner' || r === 'admin';
}
/** Whether `actor` may change / remove a member whose role is `target`. */
export function canActOnMember(actor, target) {
    // Owners and admins manage everyone but the owner (ownership moves only by transfer).
    return canManageOrg(actor) && target !== 'owner';
}
/** Max members (accepted + pending invites) of an org on a plan. Infinity = unlimited. */
export function teamMemberLimit(plan) {
    return PLANS[normalizePlan(plan)].teamMembers;
}
/**
 * How long the customer-visible audit log keeps entries, per plan. Paid plans keep
 * 24 months (the privacy policy's "normally no more than 24 months"); free keeps 90 days.
 * The retention cron deletes older rows; the API never returns them.
 */
export const AUDIT_LOG_RETENTION_DAYS = {
    free: 90,
    payg: 730,
    complimentary: 730,
};
export function auditLogRetentionDays(plan) {
    return AUDIT_LOG_RETENTION_DAYS[normalizePlan(plan)];
}
/**
 * Reading the audit log is a pay-as-you-go feature (site: "Audit logs on pay-as-you-go").
 * Entries are recorded on every plan, so an org that upgrades sees its history.
 */
export function auditLogAvailable(plan) {
    return PLANS[normalizePlan(plan)].features.includes('audit_logs');
}
/**
 * Org policy "require two-step verification for every member" (tenants.settings.requireMfa).
 * Members without MFA keep only what they need to turn it on (sign-in, /v1/auth/*, the org summary).
 */
export function orgRequiresMfa(settings) {
    if (!settings) {
        return false;
    }
    try {
        return JSON.parse(settings).requireMfa === true;
    }
    catch {
        return false;
    }
}
/** Team invites expire after 7 days and are single-use. */
export const TEAM_INVITE_TTL_HOURS = 168;
//# sourceMappingURL=org-roles.js.map